/**
 * api.ts — TanStack Query cho báo giá / đơn doanh nghiệp (QĐ 0178).
 *
 * Cửa máy chủ:
 *   GET    /quote-price-items              → bảng giá (ai tạo được đơn thì đọc được)
 *   POST   /quote-price-items              → thêm hạng mục      (catalog.write)
 *   PUT    /quote-price-items/{id}         → sửa hạng mục       (catalog.write)
 *   DELETE /quote-price-items/{id}         → ngừng bán hạng mục (catalog.write)
 *   POST   /quote-price-tiers/{id}/reviewed→ đánh dấu đã soát bậc giá
 *   GET    /quote-switch                   → công tắc chung "Báo giá doanh nghiệp"
 *   PUT    /quote-switch                   → bật/tắt công tắc chung (catalog.write)
 *   GET    /quote-config                   → cấu hình báo giá
 *   PUT    /quote-config                   → lưu cấu hình       (catalog.write)
 *   POST   /orders/quote/preview           → tính thử bản nháp, KHÔNG ghi gì
 *   GET    /orders/{id}/quote              → đọc báo giá của đơn
 *   PUT    /orders/{id}/quote              → ghi báo giá (version = khoá chống đè)
 *   POST   /orders/{id}/quote/issue        → phát hành bản gửi khách
 *   POST   /orders/{id}/quote/approve      → duyệt   (order.quote.approve)
 *   POST   /orders/{id}/quote/reject       → trả lại (order.quote.approve)
 *   GET    /quotes/pending                 → hàng chờ duyệt
 *   GET    /orders/{id}/sessions           → dãy buổi chụp của hợp đồng
 *   POST   /orders/{id}/sessions           → thêm buổi
 *   DELETE /orders/{id}/sessions/{sid}     → bỏ buổi chưa chụp
 *   GET    /orders/{id}/work-entries       → sổ công của buổi
 *   PUT    /orders/{id}/work-entries/*     → 6 đường ghi sổ công (ekip.write)
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";
import { orderKeys } from "../api";
import { BRANDS_KEY } from "@/features/products/catalogApi";

// ══════════ KIỂU ══════════

export type QuoteView = components["schemas"]["QuoteView"];
export type QuoteResult = components["schemas"]["QuoteResult"];
export type QuoteDraft = components["schemas"]["QuoteDraft"];
export type QuoteDraftLine = components["schemas"]["QuoteDraftLine"];
export type QuoteLine = components["schemas"]["QuoteLine"];
export type QuoteLineError = components["schemas"]["QuoteLineError"];
export type QuoteTotals = components["schemas"]["QuoteTotals"];
export type QuoteConfig = components["schemas"]["QuoteConfig"];
export type QuoteApproverView = components["schemas"]["QuoteApproverView"];
export type QuoteProfitColumn = components["schemas"]["QuoteProfitColumn"];
export type QuotePendingRow = components["schemas"]["QuotePendingRow"];
export type QuotePriceItem = components["schemas"]["QuotePriceItem"];
export type QuotePriceTier = components["schemas"]["QuotePriceTier"];
export type QuoteSaveItemInput = components["schemas"]["QuoteSaveItemInput"];
export type QuoteSwitch = components["schemas"]["QuoteSwitch"];
export type QuoteRequestForm = components["schemas"]["QuoteRequestForm"];
export type QuoteShootWindow = components["schemas"]["QuoteShootWindow"];
export type QuoteGiveGetCondition = components["schemas"]["QuoteGiveGetCondition"];
export type QuotePriceChange = components["schemas"]["QuotePriceChange"];
export type OrderSessionRef = components["schemas"]["OrderSessionRef"];
export type OrderSessionInput = components["schemas"]["OrderSessionInput"];
export type OrderWorkEntries = components["schemas"]["OrderWorkEntries"];
export type OrderWorkMakeupFaceInput = components["schemas"]["OrderWorkMakeupFaceInput"];
export type OrderWorkAssistantInput = components["schemas"]["OrderWorkAssistantInput"];
export type OrderWorkScoutInput = components["schemas"]["OrderWorkScoutInput"];
export type OrderWorkTravelInput = components["schemas"]["OrderWorkTravelInput"];

export type QuoteZone = QuoteView["zone"];
export type QuoteApprovalStatus = QuoteView["approvalStatus"];

// ══════════ KHOÁ CACHE ══════════

export const quoteKeys = {
  all: ["quote"] as const,
  priceItems: (params: { line?: string; includeInactive?: boolean }) =>
    ["quote", "price-items", params] as const,
  quoteSwitch: ["quote", "switch"] as const,
  config: ["quote", "config"] as const,
  detail: (orderId: string) => ["quote", "detail", orderId] as const,
  pending: (zone?: QuoteZone) => ["quote", "pending", zone ?? "all"] as const,
  sessions: (orderId: string) => ["quote", "sessions", orderId] as const,
  workEntries: (orderId: string) => ["quote", "work-entries", orderId] as const,
};

/**
 * Huỷ hiệu lực cache sau khi báo giá của một đơn đổi.
 *
 * Quét cả `orderKeys` vì tiền của đơn (tổng, còn nợ, nhãn danh sách) do chính
 * báo giá quyết định: ghi báo giá xong mà chỉ nạp lại thẻ báo giá thì màn đơn
 * và danh sách vẫn hiện tổng cũ cho tới khi tải lại trang.
 */
export function invalidateQuote(qc: QueryClient, orderId: string) {
  void qc.invalidateQueries({ queryKey: quoteKeys.detail(orderId) });
  void qc.invalidateQueries({ queryKey: quoteKeys.all });
  void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
  void qc.invalidateQueries({ queryKey: orderKeys.all });
}

// ══════════ BẢNG GIÁ ══════════

export function useQuotePriceItems(
  params: { line?: string; includeInactive?: boolean } = {},
  enabled = true,
) {
  const { line, includeInactive } = params;
  return useQuery({
    queryKey: quoteKeys.priceItems(params),
    queryFn: async () => {
      const { data, error } = await api.GET("/quote-price-items", {
        params: {
          query: {
            ...(line ? { line } : {}),
            ...(includeInactive ? { includeInactive: "true" as const } : {}),
          },
        },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được bảng giá"));
      return data.items;
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useSaveQuotePriceItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: QuoteSaveItemInput }) => {
      if (id) {
        const { data, error } = await api.PUT("/quote-price-items/{id}", {
          params: { path: { id } },
          body: input,
        });
        if (error || !data) throw new Error(serverErrorMessage(error, "Không lưu được hạng mục"));
        return data;
      }
      const { data, error } = await api.POST("/quote-price-items", { body: input });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không thêm được hạng mục"));
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: quoteKeys.all }),
  });
}

export function useDeactivateQuotePriceItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE("/quote-price-items/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(serverErrorMessage(error, "Không ngừng bán được hạng mục"));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: quoteKeys.all }),
  });
}

export function useMarkTierReviewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tierId: string) => {
      const { error } = await api.POST("/quote-price-tiers/{id}/reviewed", {
        params: { path: { id: tierId } },
      });
      if (error) throw new Error(serverErrorMessage(error, "Không ghi nhận được"));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: quoteKeys.all }),
  });
}

// ══════════ CÔNG TẮC CHUNG (QĐ 0449) ══════════

/**
 * Công tắc chung "Báo giá doanh nghiệp". Form tạo đơn đọc nó để biết có bày
 * nút "Hợp đồng doanh nghiệp" ở bước chọn thương hiệu không — cửa mở cho ai
 * tạo được đơn, nên `enabled` chỉ để tắt khi người xem không có màn này.
 */
export function useQuoteSwitch(enabled = true) {
  return useQuery({
    queryKey: quoteKeys.quoteSwitch,
    queryFn: async (): Promise<QuoteSwitch> => {
      const { data, error } = await api.GET("/quote-switch");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đọc được công tắc báo giá doanh nghiệp"));
      return data;
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useSetQuoteSwitch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (on: boolean): Promise<QuoteSwitch> => {
      const { data, error } = await api.PUT("/quote-switch", { body: { on } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đổi được công tắc"));
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: quoteKeys.all });
      // Form tạo đơn đọc cờ báo giá của dòng từ danh mục dòng (/products/brands).
      void qc.invalidateQueries({ queryKey: BRANDS_KEY });
    },
  });
}

// ══════════ CẤU HÌNH ══════════

export function useQuoteConfig(enabled = true) {
  return useQuery({
    queryKey: quoteKeys.config,
    queryFn: async () => {
      const { data, error } = await api.GET("/quote-config");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được cấu hình báo giá"));
      return data;
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useSaveQuoteConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: QuoteConfig) => {
      const { data, error } = await api.PUT("/quote-config", { body: input });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không lưu được cấu hình"));
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: quoteKeys.all }),
  });
}

// ══════════ BÁO GIÁ CỦA MỘT ĐƠN ══════════

export function useOrderQuote(orderId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: quoteKeys.detail(orderId ?? ""),
    queryFn: async (): Promise<QuoteView | null> => {
      const { data, error, response } = await api.GET("/orders/{id}/quote", {
        params: { path: { id: orderId as string } },
      });
      // 🔴 404 = đơn CHƯA có báo giá nào — trả `null`, KHÔNG ném lỗi. Phân biệt
      // với lỗi mạng là bắt buộc: gộp hai thứ thành "chưa có" thì lúc rớt mạng
      // màn mở ra một bản TRẮNG, người ta gõ rồi bấm Lưu là đè mất bản thật.
      if (response.status === 404) return null;
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được báo giá"));
      return data;
    },
    enabled: enabled && !!orderId,
    staleTime: 15_000,
  });
}

/**
 * Tính thử bản nháp đang gõ. Không ghi gì, nên KHÔNG huỷ cache nào — dùng
 * `mutateAsync` trong ô nhập để lấy tổng tức thì.
 */
export function usePreviewQuote() {
  return useMutation({
    mutationFn: async (input: QuoteDraft & { lineCode: string; orderId?: string }) => {
      const { data, error } = await api.POST("/orders/quote/preview", { body: input });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tính thử được báo giá"));
      return data;
    },
  });
}

export function useSaveQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orderId,
      input,
    }: {
      orderId: string;
      input: QuoteDraft & { version: number; reason?: string };
    }) => {
      const { data, error } = await api.PUT("/orders/{id}/quote", {
        params: { path: { id: orderId } },
        body: input,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không lưu được báo giá"));
      return data;
    },
    onSuccess: (_d, vars) => invalidateQuote(qc, vars.orderId),
  });
}

export function useIssueQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, version }: { orderId: string; version: number }) => {
      const { data, error } = await api.POST("/orders/{id}/quote/issue", {
        params: { path: { id: orderId } },
        body: { version },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không phát hành được báo giá"));
      return data;
    },
    onSuccess: (_d, vars) => invalidateQuote(qc, vars.orderId),
  });
}

export function useDecideQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orderId,
      approve,
      version,
      note,
    }: {
      orderId: string;
      approve: boolean;
      version: number;
      note?: string;
    }) => {
      const path = approve
        ? ("/orders/{id}/quote/approve" as const)
        : ("/orders/{id}/quote/reject" as const);
      const { data, error } = await api.POST(path, {
        params: { path: { id: orderId } },
        body: { version, ...(note ? { note } : {}) },
      });
      if (error || !data) {
        throw new Error(serverErrorMessage(error, approve ? "Không duyệt được" : "Không trả lại được"));
      }
      return data;
    },
    onSuccess: (_d, vars) => invalidateQuote(qc, vars.orderId),
  });
}

export function usePendingQuotes(zone?: QuoteZone, enabled = true) {
  return useQuery({
    queryKey: quoteKeys.pending(zone),
    queryFn: async () => {
      const { data, error } = await api.GET("/quotes/pending", {
        params: { query: zone ? { zone } : {} },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được hàng chờ duyệt"));
      return data.quotes;
    },
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// ══════════ BUỔI CHỤP ══════════

export function useOrderSessions(contractId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: quoteKeys.sessions(contractId ?? ""),
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/sessions", {
        params: { path: { id: contractId as string } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách buổi"));
      return data;
    },
    enabled: enabled && !!contractId,
    staleTime: 15_000,
  });
}

export function useAddOrderSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      contractId,
      input,
    }: {
      contractId: string;
      input: OrderSessionInput;
    }) => {
      const { data, error } = await api.POST("/orders/{id}/sessions", {
        params: { path: { id: contractId } },
        body: input,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không thêm được buổi chụp"));
      return data;
    },
    onSuccess: (_d, vars) => invalidateQuote(qc, vars.contractId),
  });
}

export function useRemoveOrderSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      contractId,
      sessionId,
      reason,
    }: {
      contractId: string;
      sessionId: string;
      reason?: string;
    }) => {
      const { data, error } = await api.DELETE("/orders/{id}/sessions/{sessionId}", {
        params: { path: { id: contractId, sessionId } },
        body: reason ? { reason } : {},
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không bỏ được buổi chụp"));
      return data;
    },
    onSuccess: (_d, vars) => {
      invalidateQuote(qc, vars.contractId);
      void qc.invalidateQueries({ queryKey: orderKeys.detail(vars.sessionId) });
    },
  });
}

// ══════════ SỔ CÔNG ══════════

export function useOrderWorkEntries(orderId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: quoteKeys.workEntries(orderId ?? ""),
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/work-entries", {
        params: { path: { id: orderId as string } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được sổ công"));
      return data;
    },
    enabled: enabled && !!orderId,
    staleTime: 15_000,
  });
}

/**
 * Sáu đường ghi sổ công dùng chung một hook.
 *
 * Máy chủ trả về CẢ sổ công sau khi ghi, nên đặt thẳng kết quả vào cache thay
 * vì chờ vòng tải lại — nút vừa bấm xong là con số trên màn đã đúng.
 */
export function useSaveWorkEntries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { orderId: string } & WorkEntryPayload) => {
      const { orderId } = vars;
      const path = { path: { id: orderId } };
      let out: OrderWorkEntries | undefined;
      let err: unknown;
      switch (vars.kind) {
        case "makeup-faces": {
          const r = await api.PUT("/orders/{id}/work-entries/makeup-faces", {
            params: path,
            body: { entries: vars.entries },
          });
          out = r.data;
          err = r.error;
          break;
        }
        case "assistants": {
          const r = await api.PUT("/orders/{id}/work-entries/assistants", {
            params: path,
            body: { entries: vars.entries },
          });
          out = r.data;
          err = r.error;
          break;
        }
        case "scouts": {
          const r = await api.PUT("/orders/{id}/work-entries/scouts", {
            params: path,
            body: { entries: vars.entries },
          });
          out = r.data;
          err = r.error;
          break;
        }
        case "travel": {
          const r = await api.PUT("/orders/{id}/work-entries/travel", {
            params: path,
            body: { entries: vars.entries },
          });
          out = r.data;
          err = r.error;
          break;
        }
        case "pay-sessions": {
          const r = await api.PUT("/orders/{id}/work-entries/pay-sessions", {
            params: path,
            body: { paySessions: vars.paySessions },
          });
          out = r.data;
          err = r.error;
          break;
        }
        case "meal-allowance": {
          const r = await api.PUT("/orders/{id}/work-entries/meal-allowance", {
            params: path,
            body: { enabled: vars.enabled },
          });
          out = r.data;
          err = r.error;
          break;
        }
      }
      if (err || !out) throw new Error(serverErrorMessage(err, "Không ghi được sổ công"));
      return out;
    },
    onSuccess: (data, vars) => {
      qc.setQueryData(quoteKeys.workEntries(vars.orderId), data);
      void qc.invalidateQueries({ queryKey: quoteKeys.sessions(data.contractId ?? vars.orderId) });
    },
  });
}

export type WorkEntryPayload =
  | { kind: "makeup-faces"; entries: OrderWorkMakeupFaceInput[] }
  | { kind: "assistants"; entries: OrderWorkAssistantInput[] }
  | { kind: "scouts"; entries: OrderWorkScoutInput[] }
  | { kind: "travel"; entries: OrderWorkTravelInput[] }
  | { kind: "pay-sessions"; paySessions: number }
  | { kind: "meal-allowance"; enabled: boolean };
